/**
 * Default per-repository rule allowance before applying any installation-specific overrides.
 */
export const BASE_MAX_ENABLED_RULES = 5;

/**
 * Installations with codebase scan access get a higher per-repository custom agent cap.
 */
export const SCAN_ACCESS_MAX_ENABLED_RULES = 15;

/**
 * Thresholds for determining when a custom rule should show a low acceptance badge.
 * A rule will show a low acceptance badge if it falls below both thresholds.
 */
export const MIN_TOTAL_VIOLATIONS = 10;

export const MIN_PERCENT_ADDRESSED_VIOLATIONS = 40;

/**
 * Temporary hard-coded overrides until we wire this into the superadmin dashboard.
 * Keys are GitHub installation IDs so they remain stable even if the organization renames itself.
 */
const EXTRA_CUSTOM_RULE_SLOTS_BY_INSTALLATION_ID: Record<number, number> = {
  62237188: 15, // Coral-Technologies
  79176442: 5, // myjunior
  78172223: 10, // Granola (5 base + 10 override = 15 total)
  58867123: 2, // mrge-io
  83334443: 2, // mrge-io (local)
  66966115: 5, // Rocketlane
  85287978: 15, // Personio
  110433102: 5, // pirate
  112359231: 10, // defi-wonderland (5 base + 10 override = 15 total)
  134409763: 15, // trmlabs (5 base + 15 override = 20 total)
};

type InstallationIdentifier =
  | number
  | null
  | undefined
  | {
      installationId?: number | null;
    };

const normalizeInstallationId = (installation: InstallationIdentifier): number | null => {
  if (!installation) {
    return null;
  }

  if (typeof installation === 'number') {
    return installation || null;
  }

  if (typeof installation === 'object' && 'installationId' in installation) {
    return installation.installationId ?? null;
  }

  return null;
};

/**
 * Determine how many bonus slots (if any) an installation receives beyond the base allowance.
 */
export const getExtraCustomRuleSlots = (installation: InstallationIdentifier): number => {
  const installationId = normalizeInstallationId(installation);
  if (!installationId) {
    return 0;
  }

  return EXTRA_CUSTOM_RULE_SLOTS_BY_INSTALLATION_ID[installationId] ?? 0;
};

/**
 * Compute the total number of enabled rules permitted for an installation after overrides.
 */
export const getMaxEnabledRules = (installation?: InstallationIdentifier): number => {
  return BASE_MAX_ENABLED_RULES + getExtraCustomRuleSlots(installation ?? null);
};

/**
 * Compute the total number of enabled rules permitted for an installation, applying the
 * scan-access cap uplift when applicable.
 */
export const getMaxEnabledRulesForScanAccess = (
  installation: InstallationIdentifier,
  hasScanAccess: boolean,
): number => {
  const baseLimit = getMaxEnabledRules(installation);
  if (!hasScanAccess) {
    return baseLimit;
  }

  return Math.max(baseLimit, SCAN_ACCESS_MAX_ENABLED_RULES);
};

/**
 * Convenience helper for feature toggles that behave differently when an override exists.
 */
export const hasCustomRuleLimitOverride = (installation: InstallationIdentifier): boolean => {
  return getExtraCustomRuleSlots(installation) > 0;
};
